import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ProductImageFallback } from '@/components/ProductImageFallback';
import { TableCellWithTooltip, TableRow } from '@/components/ui/Table';
import { ROUTE_PATHS } from '@/config/constants';
import { useUpdateProductSeo } from '@/hooks/useUpdateProductSeo';
import { useUpdateToneOfVoice } from '@/hooks/useUpdateToneOfVoice';
import {
  FormattedSeo,
  UpdateProductSeoDto,
  UpdateToneOfVoiceDto,
} from '@/services/ProductsService';

interface ProductRowProps {
  internalId: number;
  productId: string;
  sku: string;
  formattedSeo: FormattedSeo;
  toneOfVoiceHeaders: string[];
  toneOfVoiceNames: string[];
}

export function ProductRow({
  formattedSeo,
  internalId,
  productId,
  sku,
  toneOfVoiceHeaders,
  toneOfVoiceNames,
}: ProductRowProps) {
  const [imageError, setImageError] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.SeoContent.components.ProductsTable',
  });

  const { updateProductSeo, isLoading: isSeoLoading } = useUpdateProductSeo();
  const { updateToneOfVoice, isLoading: isToneOfVoiceLoading } =
    useUpdateToneOfVoice();

  const isLoading = isSeoLoading || isToneOfVoiceLoading;

  async function handleSaveSeo(
    updateProductSeoDto: Omit<UpdateProductSeoDto, 'productId'>,
  ) {
    try {
      await updateProductSeo({
        ...updateProductSeoDto,
        productId,
      });

      toast.success(t('updateSeoSuccessToast'));
    } catch {
      toast.error('updateSeoErrorToast');
    }
  }

  async function handleSaveToneOfVoice(
    updateToneOfVoiceDto: Omit<UpdateToneOfVoiceDto, 'productId'>,
  ) {
    try {
      await updateToneOfVoice({
        productId,
        name: updateToneOfVoiceDto.name,
        property: updateToneOfVoiceDto.property,
      });

      toast.success(t('updateSeoSuccessToast'));
    } catch {
      toast.error('updateSeoErrorToast');
    }
  }

  return (
    <>
      <TableRow>
        <TableCellWithTooltip>{productId}</TableCellWithTooltip>

        <TableCellWithTooltip
          content={formattedSeo.name}
          actions
          isSaving={isLoading}
          onSaveAsync={(value) => handleSaveSeo({ name: value })}
        >
          {formattedSeo.name}
        </TableCellWithTooltip>

        <TableCellWithTooltip>{sku}</TableCellWithTooltip>

        <TableCellWithTooltip>
          <Link
            to={ROUTE_PATHS.VISION_PRODUCT.replace(
              ':productId',
              String(internalId),
            )}
            target="_blank"
          >
            {imageError ? (
              <ProductImageFallback />
            ) : (
              <img
                src={formattedSeo?.images?.[0]?.url}
                alt={formattedSeo.name}
                onError={() => setImageError(true)}
              />
            )}
          </Link>
        </TableCellWithTooltip>

        <TableCellWithTooltip
          content={formattedSeo.description}
          actions
          isSaving={isLoading}
          onSaveAsync={(value) => handleSaveSeo({ description: value })}
        />

        <TableCellWithTooltip
          content={formattedSeo.seoDescription}
          actions
          isSaving={isLoading}
          onSaveAsync={(value) => handleSaveSeo({ seoDescription: value })}
        >
          {formattedSeo.seoDescription}
        </TableCellWithTooltip>

        <TableCellWithTooltip
          content={formattedSeo.tags}
          actions
          isSaving={isLoading}
          onSaveAsync={(value) => handleSaveSeo({ tags: value })}
        >
          {formattedSeo.tags}
        </TableCellWithTooltip>

        {toneOfVoiceHeaders.length > 0 &&
          toneOfVoiceNames.length === 0 &&
          Array.from({ length: toneOfVoiceHeaders.length }).map((_, index) => (
            <TableCellWithTooltip key={index}></TableCellWithTooltip>
          ))}

        {toneOfVoiceNames.length > 0 &&
          toneOfVoiceNames.map((item, index) => (
            <TableCellWithTooltip
              key={item}
              className="[&_.line-clamp-3]:w-24"
              content={item}
              actions
              isSaving={isLoading}
              onSaveAsync={(value) =>
                handleSaveToneOfVoice({
                  name: value,
                  property: toneOfVoiceHeaders[index],
                })
              }
            >
              {item}
            </TableCellWithTooltip>
          ))}

        {formattedSeo.images.map((image, index) => (
          <TableCellWithTooltip
            key={index}
            content={image.content}
            actions
            isSaving={isLoading}
            onSaveAsync={(value) =>
              handleSaveSeo({ imageContent: value, imageUrl: image.url })
            }
          >
            {image.content}
          </TableCellWithTooltip>
        ))}
      </TableRow>
    </>
  );
}
